/**
 * Per-workshop bubble diagram for the dashboard grid slot that WS4
 * fills with the specialised HAW modes panel. Every other workshop
 * reuses its Mission Control bubble layout, but the visual treatment
 * in this smaller slot should match the WS4 workshop bubbles rather
 * than the large Mission Control/home-page bubble chrome.
 *
 * That means:
 *   - every bubble in a workshop uses that workshop's workstream accent
 *     (same token as the WS tag / navigator band)
 *   - workshop dashboard bubbles stay title-only
 *   - fills are soft accent tints instead of MC radial gradients
 *   - no MC glow/filter chrome or icon stack
 *   - dashed rings spin at the same 70s pace as the WS4 HAW panel
 *
 * Home/Mission Control panels still use their original rendering path.
 */

import { memo, useMemo, type CSSProperties } from "react";
import { Layers } from "lucide-react";
import { Bubble } from "@/components/diagrams/Bubble";
import type { BubbleConfig } from "@/components/diagrams/types";
import { DASHBOARD_BUBBLE_LAYOUT } from "@/components/diagrams/types";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import {
  WORKSHOP_PANEL_DATA,
  type AccentKey,
  type WorkshopPanelData,
} from "@/features/missionControl/workshop-panel-data";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { resolveWorkshopDiagram } from "@/features/workshop/resolve-workshop-diagram";

const ACCENT_VAR: Record<AccentKey, string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
};

const ACCENT_CSS_VAR: Record<AccentKey, string> = {
  "true-blue": "--accent-true-blue",
  sky: "--accent-sky",
  gold: "--accent-gold",
  melon: "--accent-melon",
};

/** Enlarge in-bubble copy for the smaller dashboard cell without
 *  changing ring positions (connectors stay aligned). */
const TYPE_SCALE = 1.45;

const HAW_RING_SPEED_STYLE = {
  "--bubble-ring-speed": "70s",
} as unknown as CSSProperties;

function scaleBubbleForDashboard(
  bubble: BubbleConfig,
  stroke: string,
): BubbleConfig {
  return {
    ...bubble,
    // Dashboard slot is tight — titles only. Tip copy still carries the
    // longer body on hover. Mission Control/home keep the full bubble text.
    bodyLines: [],
    icon: undefined,
    titleFontSize: Math.round(bubble.titleFontSize * TYPE_SCALE * 10) / 10,
    bodyFontSize: bubble.bodyFontSize,
    iconScale: 0,
    iconTx: bubble.cx,
    letterSpacing: Math.max(0.2, bubble.letterSpacing * 0.75),
    dividerHW: bubble.dividerHW * 1.05,
    outerStrokeW: 1.5,
    ringStrokeW: 1,
    outerOp: 0.7,
    ringOp: 0.4,
    stroke,
    fill: `color-mix(in srgb, ${stroke} 6%, transparent)`,
    filter: "",
  };
}

function WorkshopThemeDiagramImpl({
  workshopId,
}: {
  workshopId: WorkshopPanelData["workshopId"];
}) {
  const data = WORKSHOP_PANEL_DATA[workshopId];
  const override = useEffectiveWorkshopOverride(workshopId);
  const resolved = useMemo(
    () => resolveWorkshopDiagram(workshopId, override),
    [workshopId, override],
  );
  const accentVar = ACCENT_VAR[data.accent];
  const title = data.brief;
  const bubbles = useMemo(
    () => resolved.bubbles.map((bubble) => scaleBubbleForDashboard(bubble, accentVar)),
    [resolved.bubbles, accentVar],
  );
  const ariaLabel = `${data.workshopId} — ${resolved.bubbles
    .map((b) => b.titleLines.join(" "))
    .join(", ")}`;

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-title">
          <Layers className="panel-title-icon" aria-hidden />
          <span className="truncate">{title}</span>
        </div>
        <div className="panel-menu" aria-hidden>
          ···
        </div>
      </div>
      <div
        className="panel-body relative flex min-h-0 flex-col"
        style={{ padding: "20px 14px" }}
      >
        <SmokeCanvas
          className="pointer-events-none absolute inset-0 h-full w-full"
          cssVarName={ACCENT_CSS_VAR[data.accent]}
          ariaLabel={`${workshopId} ambient smoke field`}
        />
        <div className="relative block h-full w-full min-h-0 flex-1">
          <svg
            viewBox={resolved.viewBox}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            className="block h-full w-full"
            style={HAW_RING_SPEED_STYLE}
            role="img"
            aria-label={ariaLabel}
          >
            {resolved.connectors.map(([x1, y1, x2, y2], i) => (
              <line
                key={`con-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={accentVar}
                strokeOpacity={0.35}
                strokeWidth={1}
                strokeDasharray="4,3"
              />
            ))}
            {bubbles.map((config) => (
              <Bubble
                key={config.id}
                config={config}
                layout={DASHBOARD_BUBBLE_LAYOUT}
              />
            ))}
          </svg>
        </div>
      </div>
      <div
        className="haw-status text-muted"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          fontSize: "var(--fs-label)",
        }}
      >
        <span
          className="flex-shrink-0"
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: accentVar,
            boxShadow: `0 0 8px ${accentVar}`,
            animation: "pulse 2s infinite",
          }}
        />
        {data.workshopId} theme
      </div>
    </div>
  );
}

export const WorkshopThemeDiagram = memo(WorkshopThemeDiagramImpl);
